import { JSX, ReactNode } from 'react';
import {
  Button,
  ButtonProps,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';

interface ConfirmationDialogProps {
  /**
   * Text for the cancel button
   * @default 'Cancel'
   */
  cancelText?: string;
  /**
   * Color for the confirm button
   * @default 'danger'
   */
  confirmColor?: ButtonProps['color'];
  /**
   * Text for the confirm button
   * @default 'Confirm'
   */
  confirmText?: string;
  content: ReactNode;
  /**
   * Hides the confirm button
   * @default false
   */
  hideConfirmButton?: boolean;
  /**
   * Disables the confirm button
   */
  isDisabled?: boolean;
  isOpen: boolean;
  /**
   * Shows a loading state on the confirm button
   * @default false
   */
  isPending?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
}

export function ConfirmationDialog(props: ConfirmationDialogProps): JSX.Element {
  const {
    cancelText = 'Cancel',
    confirmColor = 'danger',
    confirmText = 'Confirm',
    content,
    hideConfirmButton = false,
    isDisabled = false,
    isOpen,
    isPending,
    onClose,
    onConfirm,
    title,
  } = props;

  const handleConfirm = (): void => {
    onConfirm();
    onClose();
  };

  return (
    <Modal
      backdrop="blur"
      className="border-1 border-white/20"
      classNames={{
        body: 'gap-0',
      }}
      data-testid="ConfirmationDialog"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 text-foreground">{title}</ModalHeader>
        <ModalBody className="text-foreground">{content}</ModalBody>
        <ModalFooter>
          <Button isDisabled={isPending} onPress={onClose} variant="bordered">
            {cancelText}
          </Button>
          {!hideConfirmButton && (
            <Button
              color={confirmColor}
              isDisabled={isDisabled}
              isLoading={isPending}
              onPress={handleConfirm}
            >
              {confirmText}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
